// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useNavigate }     from 'react-router-dom'
import { ArrowLeft, ArrowRight, CheckCircle2, LayoutDashboard } from 'lucide-react'
import { SaveStatusBadge } from '@/components/ui/SaveStatusBadge'
import { PrimaryActionButton } from '@/components/ui/PrimaryActionButton'
import { ROUTES, HOME_ROUTE } from '@/lib/constants/routes'
import type { SaveStatus } from '@/hooks/useAutosave'

export const STEP_LABELS = [
  // Persoonlijk & netwerk (0–4)
  'Persoonlijke gegevens', 'Partner', 'Vertrouwenspersoon', 'Kinderen & voogdij', 'Huisdieren',
  // Bezittingen (5–11)
  'Wonen', 'Tweede woning', 'Extra locaties', 'Onderneming', 'Voertuigen & mobiliteit', 'Financieel', 'Aandelen & beleggingen',
  // Persoonlijke wensen (12–17)
  'Donorregistratie', 'Medische wensen', 'Testament', 'Levenstestament', 'Uitvaartwensen', 'Uitvaartverzekering & kosten',
  // Nalatenschap & afsluiting (18–22)
  'Digitale nalatenschap', 'Documentwijzer', 'Persoonlijk afscheid', 'Berichtenservice', 'Controle & afronding',
]

// V53: korte, per stap getailorde begeleidingszin onder het staplabel.
// Maakt van de wizard één doorlopend gesprek. Niet overal dezelfde zin; rustig, positief, B1.
// Beslis-stappen dragen subtiel "nu u dat zelf bepaalt"; bezit-/informatiestappen "zo helpt u uw naasten".
// 18–20 zijn embedded pagina's met een eigen intro -> leeg laten (geen dubbeling).
export const STEP_SUBTITLES = [
  'We beginnen rustig met de basisgegevens over uzelf.',
  'Leg vast wat uw partner later moet kunnen vinden.',
  'Kies nu zelf wie u vertrouwt met belangrijke zaken.',
  'U bepaalt hoe uw kinderen straks verzorgd worden.',
  'Zorg dat uw dieren in goede handen blijven.',
  'Uw woonsituatie helder, zodat uw naasten het overzien.',
  'Een tweede woning brengt extra zaken om te regelen.',
  'Andere plekken waar belangrijke spullen of papieren liggen.',
  'Regel nu wat er met uw onderneming moet gebeuren.',
  'Uw voertuigen, zodat uw naasten weten wat er speelt.',
  'Breng uw geldzaken in beeld nu u het overzicht heeft.',
  'Leg vast wat voor uw naasten anders lastig te vinden is.',
  'Uw keuze over orgaandonatie, door uzelf bepaald.',
  'Uw medische wensen, zolang u die zelf kunt aangeven.',
  'Belangrijke keuzes rond uw testament, nu u ze zelf maakt.',
  'Regel dit nu het rustig en weloverwogen kan.',
  'U bepaalt hoe u herdacht wilt worden.',
  'Uitvaartkosten en verzekering, alvast helder geregeld.',
  '',
  '',
  '',
  'Berichten die later met uw naasten gedeeld mogen worden.',
  'U bent er bijna. Bekijk rustig wat u heeft vastgelegd.',
]

interface WizardLayoutProps {
  currentStep: number
  totalSteps:  number
  saveStatus?: SaveStatus
  canNext?:    boolean
  hideNav?:    boolean   // verberg de navigatiebalk volledig (bijv. als stap eigen nav heeft)
  onNext:      () => void
  onPrev:      () => void
  children:    React.ReactNode
}

export function WizardLayout({
  currentStep,
  totalSteps,
  saveStatus = 'idle',
  canNext    = true,
  hideNav    = false,
  onNext,
  onPrev,
  children,
}: WizardLayoutProps) {
  const navigate = useNavigate()
  const isFirst  = currentStep === 0
  const isLast   = currentStep === totalSteps - 1
  const label    = STEP_LABELS[currentStep] ?? ''

  // Categoriekleur per stap - Optie 2: zachte achtergrond + 3px linker accentlijn
  // Stappen 0–3: Gezin (amber), 4–8: Bezittingen (blauw), 9: Vertrouwenspersoon (groen),
  // 10–13: Zorg & wensen (roze), 14–16: Afscheid & afsluiting (violet)
  const stepStyle = (() => {
    if (currentStep <= 4)  return { bg: 'bg-amber-50',  border: 'border-l-[3px] border-l-amber-600',  title: 'text-amber-900',  sub: 'text-amber-700'  }
    if (currentStep <= 11) return { bg: 'bg-blue-50',   border: 'border-l-[3px] border-l-blue-600',   title: 'text-blue-900',   sub: 'text-blue-700'   }
    if (currentStep <= 17) return { bg: 'bg-pink-50',   border: 'border-l-[3px] border-l-pink-600',   title: 'text-pink-900',   sub: 'text-pink-700'   }
    if (currentStep <= 21) return { bg: 'bg-teal-50',   border: 'border-l-[3px] border-l-teal-600',   title: 'text-teal-900',   sub: 'text-teal-700'   }
    return                        { bg: 'bg-violet-50', border: 'border-l-[3px] border-l-violet-600', title: 'text-violet-900', sub: 'text-violet-700' }
  })()

  return (
    /*
      fixed inset-0 ensures the wizard covers the entire viewport,
      regardless of any parent layout or scroll context.
      z-50 ensures it sits above any potential AppShell remnant.
    */
    <div className="fixed inset-0 z-50 flex flex-col bg-sand overflow-hidden">

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <header className="flex-shrink-0 flex items-center justify-between
                         px-5 py-4 bg-white border-b border-sand-200"
              style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top))' }}>
        {/* ← Terug naar begin */}
        <button
          onClick={() => navigate(HOME_ROUTE, { state: { savedFromWizard: true } })}
          className="flex items-center gap-1.5 text-sm text-ink-4 hover:text-ink-3 transition-colors"
          title="Terug naar beginscherm - gegevens zijn opgeslagen"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Terug naar begin</span>
          <span className="sm:hidden">Terug</span>
        </button>

        {/* Step label + save status */}
        <div className="flex items-center gap-3">
          <SaveStatusBadge status={saveStatus} />
          <span className="hidden sm:block text-xs text-ink-4">
            Stap {currentStep + 1} van {totalSteps} · {Math.round(((currentStep + 1) / totalSteps) * 100)}%
          </span>
        </div>

        {/* Later afronden */}
        <button
          onClick={() => navigate(ROUTES.DASHBOARD)}
          className="flex items-center gap-1.5 text-xs text-ink-4
                     hover:text-ink-3 transition-colors"
          title="Later afronden"
        >
          <LayoutDashboard className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Later afronden</span>
        </button>
      </header>

      {/* ── Voortgangsbalk ───────────────────────────────────────────────── */}
      <div className="flex-shrink-0 h-1 w-full bg-sand-200" role="progressbar"
        aria-valuenow={Math.round(((currentStep + 1) / totalSteps) * 100)} aria-valuemin={0} aria-valuemax={100}
        aria-label={`Stap ${currentStep + 1} van ${totalSteps}`}>
        <div className="h-full bg-sage-600 transition-all duration-500 ease-out"
          style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }} />
      </div>

      {/* ── Stap label - met categoriekleur (Optie 2) ───────────────────── */}
      <div className={`flex-shrink-0 border-b border-sand-200 px-5 py-3 ${stepStyle.bg} ${stepStyle.border}`}>
        <div className="max-w-xl mx-auto">
          <span className={`text-sm font-semibold ${stepStyle.title}`}>{label}</span>
          {STEP_SUBTITLES[currentStep] && (
            <p className={`text-xs mt-0.5 leading-relaxed ${stepStyle.sub}`}>{STEP_SUBTITLES[currentStep]}</p>
          )}
        </div>
      </div>

      {/* ── Scrollable content ───────────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-xl mx-auto px-5 py-6 pb-[calc(7rem+env(safe-area-inset-bottom))] animate-fade-in">
          {children}
        </div>
      </main>

      {/* ── Navigation bar (sticky bottom) ──────────────────────────────── */}
      {!hideNav && (
      <div className="flex-shrink-0 bg-white/95 backdrop-blur-sm border-t border-sand-200 px-5 pt-4"
           style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
        <div className="max-w-xl mx-auto flex items-stretch gap-3">

          {/* Vorige */}
          {!isFirst && (
            <button
              onClick={onPrev}
              className="flex items-center gap-2 rounded-2xl border-2 border-sand-300
                         bg-white px-5 py-4 text-sm font-semibold text-ink-3
                         hover:border-sage-300 hover:text-ink-2
                         transition-all active:scale-[0.98] flex-shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
              Vorige
            </button>
          )}

          {/* Volgende / Afronden */}
          <PrimaryActionButton
            onClick={onNext}
            disabled={!canNext}
            fullWidth={false}
            className="flex-1"
            icon={isLast ? <CheckCircle2 className="h-5 w-5" /> : undefined}
            iconRight={isLast ? undefined : <ArrowRight className="h-5 w-5" />}
          >
            {isLast ? 'Naar dashboard' : 'Volgende'}
          </PrimaryActionButton>
        </div>

        {/* Later afronden - mobile link */}
        <div className="max-w-xl mx-auto mt-3 text-center sm:hidden">
          <button
            onClick={() => navigate(ROUTES.DASHBOARD)}
            className="text-xs text-ink-4 underline-offset-2 hover:underline"
          >
            Later afronden - naar dashboard
          </button>
        </div>
      </div>
      )}
    </div>
  )
}
